/**
 * Import Units View
 * 
 * Handles importing units from a connected channel.
 * Now uses module's own hooks and services.
 */

import { FC, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ToastMessageContext } from '@context';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useChannelUnitsForImportQuery, useImportUnitsMutation } from '../../hooks';
import { ChannelName, IChannelProperty, SyncStatus } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import {
  SuccessStep,
  ImportPropertiesStep,
  ImportingModal,
  ImportUnitsHeader,
} from '../../components';
import { ConnectPageContainer } from '../../styles';

// Valid channel names
const VALID_CHANNELS: ChannelName[] = ['airbnb', 'booking', 'gathern'];

const ImportUnitsView: FC = () => {
  const navigate = useNavigate();
  const { integration } = useParams<{ integration: string }>();
  const [searchParams] = useSearchParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Get channelId from query params
  const channelId = searchParams.get('channelId') || searchParams.get('channel_id');
  
  // Validate channel name
  const channelName = integration as ChannelName;
  const isValidChannel = VALID_CHANNELS.includes(channelName);
  
  // For Airbnb and Gathern, channelId is required
  const isChannelIdRequired = channelName === 'airbnb' || channelName === 'gathern';
  const hasRequiredChannelId = !isChannelIdRequired || !!channelId;
  
  // Validation error state
  const validationError = useMemo(() => {
    if (!isValidChannel) {
      return {
        titleKey: T_KEYS.INVALID_INTEGRATION,
        messageKey: T_KEYS.INVALID_INTEGRATION_DESC,
        messageParams: { integration },
      };
    }
    if (isChannelIdRequired && !channelId) {
      const channelDisplayName = channelName === 'gathern' ? 'Gathern' : 'Airbnb';
      return {
        titleKey: T_KEYS.MISSING_CHANNEL_ID,
        messageKey: T_KEYS.MISSING_CHANNEL_ID_DESC,
        messageParams: { channel: channelDisplayName },
      };
    }
    return null;
  }, [isValidChannel, isChannelIdRequired, channelId, integration, channelName, t]);

  // Show success step only when arriving from the connection flow
  const isNewConnection = searchParams.get('newConnection') === 'true';

  // State
  const [currentStep, setCurrentStep] = useState<'success' | 'import'>(isNewConnection ? 'success' : 'import');
  const [properties, setProperties] = useState<IChannelProperty[]>([]);
  const [importingIds, setImportingIds] = useState<string[]>([]);
  const [showImportingModal, setShowImportingModal] = useState(false);
  const [importModalStatus, setImportModalStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [lastImportedIds, setLastImportedIds] = useState<string[]>([]);
  const [importedUserChannelId, setImportedUserChannelId] = useState<number | null>(null);

  // Fetch units for import using module hook
  const { 
    data: unitsData, 
    isLoading: isUnitsLoading,
    error: unitsError,
    refetch: refetchUnits,
  } = useChannelUnitsForImportQuery(channelName, channelId);

  // Import mutation using module hook
  const { mutate: importUnits, isPending: isImporting } = useImportUnitsMutation();

  // Extract userChannelId and channelType from the query result
  const userChannelId = unitsData?.userChannelId ?? null;
  const apiChannelType = unitsData?.channelType ?? null;

  // Transform units data to IChannelProperty
  useEffect(() => {
    if (unitsData?.units) {
      setProperties(
        unitsData.units.map((unit) => ({
          id: unit.id,
          externalId: unit.propertyId,
          name: unit.name,
          location: unit.address,
          imageUrl: unit.imageUrl || undefined,
          isImported: unit.isConnected,
          channelName: channelName,
          syncStatus: unit.syncStatus,
          syncStatusName: unit.syncStatusName,
          syncStatusNameAr: unit.syncStatusNameAr,
        }))
      );
    }
  }, [unitsData, channelName]);

  // Handle import now
  const handleImportNow = useCallback(() => {
    setCurrentStep('import');
  }, []);

  // Handle import later — navigate to the channel's manage page
  const handleImportLater = useCallback(() => {
    navigate(`/channels/${channelName}/manage`);
  }, [navigate, channelName]);

  // Ref to track modal timeout
  const modalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref to track if modal is shown (to avoid stale closure issues)
  const isModalShownRef = useRef(false);

  // Handle start import
  const handleStartImport = useCallback((propertyIds: string[]) => {
    if (!userChannelId || apiChannelType === null) {
      showError(t(T_KEYS.CHANNEL_DATA_NOT_AVAILABLE));
      return;
    }

    setImportingIds(propertyIds);
    setLastImportedIds(propertyIds);
    setImportModalStatus('loading');

    // Reset modal ref
    isModalShownRef.current = false;
    
    // Show modal after 1 second delay (button shows loading state immediately)
    modalTimeoutRef.current = setTimeout(() => {
      isModalShownRef.current = true;
      setShowImportingModal(true);
    }, 1000);

    importUnits(
      { 
        userChannelId, 
        channelType: apiChannelType, 
        listings: unitsData?.units.filter(unit => propertyIds.includes(unit.id)).map(unit => ({listingId: unit.id, listingName: unit.name, imageUrl: unit.imageUrl ?? null})) || []
      },
      {
        onSuccess: (result) => {
          // Clear timeout if import succeeded before 1 second
          if (modalTimeoutRef.current) {
            clearTimeout(modalTimeoutRef.current);
            modalTimeoutRef.current = null;
          }
          
          setImportingIds([]);
          // Store the userChannelId from the response for navigation
          if (result.userChannelId) {
            setImportedUserChannelId(result.userChannelId);
          }
          // Optimistic update: mark only the selected/imported units as imported with proper status
          setProperties((prev) =>
            prev.map((p) =>
              propertyIds.includes(p.id) 
                ? { 
                    ...p, 
                    isImported: true,
                    syncStatus: SyncStatus.Imported,
                    syncStatusName: 'Imported',
                    syncStatusNameAr: 'تم الاستيراد'
                  } 
                : p
            )
          );
          
          // Use ref to check if modal is shown (avoids stale closure issue)
          if (!isModalShownRef.current) {
            const successMessage = isRtl ? result.messageAr : result.message;
            showSuccess(successMessage || t(T_KEYS.IMPORT_SUCCESS));
          } else {
            setImportModalStatus('success');
          }
        },
        onError: (error) => {
          setImportingIds([]);
          setImportModalStatus('error');
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMessage = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message)
            : errorWithAr.message;
          showError(errorMessage || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [userChannelId, apiChannelType, importUnits, unitsData, showSuccess, showError, t, isRtl]);

  // Handle retry import
  const handleRetryImport = useCallback(() => {
    if (lastImportedIds.length > 0) {
      handleStartImport(lastImportedIds);
    }
  }, [lastImportedIds, handleStartImport]);

  // Handle go to channels/account page
  const handleGoToChannels = useCallback(() => {
    if (importModalStatus === 'success') {
      showSuccess(t(T_KEYS.IMPORT_SUCCESS));
    }
    // Navigate to the account page if we have the userChannelId from import response
    if (importedUserChannelId) {
      navigate(`/channels/${channelName}/manage?accountId=${importedUserChannelId}`);
    } else {
      navigate('/channels');
    }
  }, [navigate, importModalStatus, showSuccess, t, importedUserChannelId, channelName]);

  // Handle stay here
  const handleModalStayHere = useCallback(() => {
    if (importModalStatus === 'success') {
      showSuccess(t(T_KEYS.IMPORT_SUCCESS));
    }
    setShowImportingModal(false);
    setImportingIds([]);
  }, [importModalStatus, showSuccess, t]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Get channel display name
  const channelDisplayName = useMemo(() => {
    if (!isValidChannel) return '';
    return isRtl ? CHANNEL_INFO[channelName].displayNameAr : CHANNEL_INFO[channelName].displayName;
  }, [isValidChannel, isRtl, channelName]);

  // Render error state for validation errors
  if (validationError) {
    return (
      <ConnectPageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            gap: 3,
            p: 4,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={600} color="error.main">
            {t(validationError.titleKey)}
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={500}>
            {t(validationError.messageKey, validationError.messageParams)}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/channels')}
            sx={{ mt: 2 }}
          >
            {t(T_KEYS.BACK_TO_CHANNELS)}
          </Button>
        </Box>
      </ConnectPageContainer>
    );
  }

  // Render error state for API errors
  if (unitsError) {
    const errorWithAr = unitsError as Error & { errorMessageAr?: string };
    const errorMessage = isRtl 
      ? (errorWithAr.errorMessageAr || errorWithAr.message || 'حدث خطأ أثناء تحميل الوحدات. يرجى المحاولة مرة أخرى.')
      : (errorWithAr.message || 'An error occurred while loading units. Please try again.');
    
    return (
      <ConnectPageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            gap: 3,
            p: 4,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={600} color="error.main">
            {t(T_KEYS.ERROR_LOADING_UNITS)}
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={500}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/channels')}>
              {t(T_KEYS.BACK_TO_CHANNELS)}
            </Button>
            <Button variant="contained" onClick={() => refetchUnits()}>
              {t(T_KEYS.TRY_AGAIN)}
            </Button>
          </Box>
        </Box>
      </ConnectPageContainer>
    );
  }

  return (
    <ConnectPageContainer>
      <ImportUnitsHeader
        channelName={channelName}
        channelDisplayName={channelDisplayName}
        onBack={handleBack}
      />

      {currentStep === 'success' && (
        <>
          {isNewConnection && (
            <SuccessStep
              channelName={channelName}
              onImportNow={handleImportNow}
              onImportLater={handleImportLater}
            />
          )}
          <ImportPropertiesStep
            channelName={channelName}
            properties={properties}
            onStartImport={handleStartImport}
            onImportLater={handleImportLater}
            isLoading={isUnitsLoading}
            importingIds={importingIds}
            isImportButtonLoading={isImporting}
            isNewConnection={isNewConnection}
          />
        </>
      )}

      {currentStep === 'import' && (
        <ImportPropertiesStep
          channelName={channelName}
          properties={properties}
          onStartImport={handleStartImport}
          onImportLater={handleImportLater}
          isLoading={isUnitsLoading}
          importingIds={importingIds}
          isImportButtonLoading={isImporting}
          isNewConnection={isNewConnection}
        />
      )}

      <ImportingModal
        open={showImportingModal}
        status={importModalStatus}
        onGoToDashboard={handleGoToChannels}
        onStayHere={handleModalStayHere}
        onRetry={handleRetryImport}
      />
    </ConnectPageContainer>
  );
};

export default ImportUnitsView;
